import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Megaphone, Pencil, Pin, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

const emptyForm = { title: "", body: "", pinned: false };

export const AnnouncementsFeed = ({ isManager }) => {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/announcements`, { withCredentials: true });
      setItems(res.data);
    } catch (err) {
      console.error("Failed to load announcements", err);
      setItems([]);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ title: item.title, body: item.body, pinned: item.pinned });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/announcements/${editing.id}`, form, { withCredentials: true });
        toast.success("Announcement updated.");
      } else {
        await axios.post(`${API}/announcements`, form, { withCredentials: true });
        toast.success("Announcement posted.");
      }
      setOpen(false);
      fetchItems();
    } catch (err) {
      console.error("Failed to save announcement", err);
      toast.error("Could not save the announcement.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/announcements/${id}`, { withCredentials: true });
      toast.success("Announcement deleted.");
      fetchItems();
    } catch (err) {
      console.error("Failed to delete announcement", err);
      toast.error("Could not delete the announcement.");
    }
  };

  return (
    <div data-testid="announcements-feed">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xs uppercase tracking-[0.3em] text-[#B8997A]">Announcements</h2>
        {isManager && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                onClick={openCreate}
                data-testid="announcement-new-button"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider border border-foreground/25 px-4 py-2 hover:border-[#B8997A] hover:text-[#B8997A] transition-colors"
              >
                <Plus size={13} strokeWidth={1.5} /> New
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#F7F5F0] max-w-lg" data-testid="announcement-dialog">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  {editing ? "Edit Announcement" : "New Announcement"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 mt-2">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em]">Title</Label>
                  <Input
                    data-testid="announcement-input-title"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="bg-transparent border-foreground/25"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em]">Body</Label>
                  <Textarea
                    data-testid="announcement-input-body"
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    rows={5}
                    className="bg-transparent border-foreground/25 resize-none"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    data-testid="announcement-switch-pinned"
                    checked={form.pinned}
                    onCheckedChange={(v) => setForm({ ...form, pinned: v })}
                  />
                  <Label className="text-xs uppercase tracking-[0.2em]">Pinned</Label>
                </div>
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="announcement-save-button"
                  className="inline-flex items-center gap-2 bg-foreground text-background px-8 py-3 text-sm font-medium hover:bg-[#B8997A] hover:text-[#0B0F14] transition-colors disabled:opacity-60"
                >
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  {editing ? "Save Changes" : "Post Announcement"}
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items === null ? (
        <div className="flex justify-center py-10">
          <Loader2 size={22} className="animate-spin bronze" />
        </div>
      ) : items.length === 0 ? (
        <div className="border border-foreground/15 p-8 text-center" data-testid="announcements-empty">
          <Megaphone size={22} strokeWidth={1.25} className="bronze mx-auto" />
          <p className="mt-3 font-display text-lg font-medium">No announcements yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Company announcements will appear here.</p>
        </div>
      ) : (
        <div className="space-y-px bg-border border border-foreground/10">
          {items.map((a) => (
            <article key={a.id} className="bg-background p-6" data-testid={`announcement-item-${a.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-xl font-medium tracking-tight flex items-center gap-2">
                    {a.pinned && <Pin size={14} strokeWidth={1.5} className="bronze shrink-0" data-testid={`announcement-pinned-${a.id}`} />}
                    {a.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{a.body}</p>
                  <p className="mt-3 text-xs text-muted-foreground/70">
                    {a.author_name || a.author_email} · {fmtDate(a.created_at)}
                  </p>
                </div>
                {isManager && (
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => openEdit(a)}
                      data-testid={`announcement-edit-${a.id}`}
                      aria-label="Edit announcement"
                      className="p-2 border border-foreground/15 hover:border-[#B8997A] hover:text-[#B8997A] transition-colors"
                    >
                      <Pencil size={13} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => remove(a.id)}
                      data-testid={`announcement-delete-${a.id}`}
                      aria-label="Delete announcement"
                      className="p-2 border border-foreground/15 hover:border-destructive hover:text-destructive transition-colors"
                    >
                      <Trash2 size={13} strokeWidth={1.5} />
                    </button>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};
